import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * The card the admin and ops dashboards are built out of.
 *
 * ── The shape, transcribed ──────────────────────────────────────────────────
 *
 * 14px radius, 18px/20px padding with NO bottom padding, a 32px rounded-9 icon
 * tile at the top, a 16px title with a hairline under it, the body, and then a
 * 5px bar of colour running the full width of the card, flush to its bottom
 * edge.
 *
 * The missing bottom padding and the negative margin on the bar are the same
 * decision: the bar is meant to be part of the card's edge, not a block sitting
 * inside it. Give the card bottom padding and the bar floats, which looks like
 * a rendering bug rather than a design.
 *
 * ── Why the accent bar is worth keeping ─────────────────────────────────────
 *
 * It is what makes a row of four cards read as a set. Without it they are four
 * white rectangles and the eye has nothing to travel along; with it there is a
 * baseline. It also carries a second signal at no cost - a red-topped card with
 * a brand bar is "urgent, but in hand", which is a distinction a single colour
 * cannot make.
 *
 * ── Tones ───────────────────────────────────────────────────────────────────
 *
 * The icon tile's tint and the bar's colour are separate props on purpose. The
 * design pairs a red tile with a brand bar on the admin action cards, and that
 * is not an oversight: the tile says what kind of thing this is and the bar
 * says which set it belongs to.
 */
export type AccentTone = 'brand' | 'positive' | 'caution' | 'negative';

const TILE: Record<AccentTone, string> = {
  brand: 'bg-brand-50 text-brand-600 dark:bg-brand-600/15 dark:text-brand-300',
  positive: 'bg-pill-positive text-pill-positive-foreground',
  caution: 'bg-pill-caution text-pill-caution-foreground',
  negative: 'bg-pill-negative text-pill-negative-foreground',
};

const BAR: Record<AccentTone, string> = {
  brand: 'bg-brand-700',
  positive: 'bg-pill-positive-foreground',
  caution: 'bg-pill-caution-foreground',
  negative: 'bg-pill-negative-foreground',
};

export interface AccentCardProps {
  /** Sits in the tinted tile. A 16px lucide icon at stroke 2. */
  icon?: ReactNode;
  /** Tints the icon tile. Says what kind of thing this is. */
  tone?: AccentTone;
  /** Colours the bottom bar. Says which set this belongs to. */
  bar?: AccentTone;
  title: ReactNode;
  children?: ReactNode;
  className?: string;
}

export function AccentCard({
  icon,
  tone = 'brand',
  bar = 'brand',
  title,
  children,
  className,
}: AccentCardProps) {
  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-[14px] border border-border bg-card px-5 pt-[18px] shadow-[0_1px_3px_rgba(15,23,42,0.04)]',
        className,
      )}
    >
      {icon && (
        <span
          className={cn(
            'flex size-8 flex-none items-center justify-center rounded-[9px]',
            TILE[tone],
          )}
          aria-hidden="true"
        >
          {icon}
        </span>
      )}

      <div
        className={cn(
          'border-b border-border pb-2 text-base font-semibold',
          icon ? 'mt-3' : '',
          'mb-3.5',
        )}
      >
        {title}
      </div>

      <div className="flex-1">{children}</div>

      {/*
        Full-bleed: negative horizontal margins cancel the card's padding so the
        bar reaches both edges, and there is no bottom padding for it to float
        above. See the note at the top - this is the detail that makes it read
        as part of the card rather than a block inside it.
      */}
      <div className={cn('mt-3 -mx-5 h-[5px]', BAR[bar])} aria-hidden="true" />
    </div>
  );
}

/**
 * A single number in an AccentCard, set the way the design sets it.
 *
 * 28px at weight 700 and -0.8px tracking, with a leading of exactly 1 - so the
 * digits sit tight and the delta line underneath is close enough to read as
 * belonging to them. A default leading here leaves a gap that makes the two
 * look like separate facts.
 */
export function AccentStat({
  value,
  delta,
  deltaTone = 'positive',
}: {
  value: ReactNode;
  /** "up 12 on last week", or similar. Omit when there is nothing to compare. */
  delta?: ReactNode;
  deltaTone?: 'positive' | 'caution' | 'negative';
}) {
  const dot =
    deltaTone === 'positive'
      ? 'bg-pill-positive-foreground'
      : deltaTone === 'caution'
        ? 'bg-pill-caution-foreground'
        : 'bg-pill-negative-foreground';
  const text =
    deltaTone === 'positive'
      ? 'text-pill-positive-foreground'
      : deltaTone === 'caution'
        ? 'text-pill-caution-foreground'
        : 'text-pill-negative-foreground';

  return (
    <>
      <div className="text-[28px] leading-none font-bold tracking-[-0.8px]">{value}</div>
      {delta && (
        <div className="mt-2 flex items-center gap-1.5">
          <span className={cn('size-[5px] flex-none rounded-full', dot)} aria-hidden="true" />
          <span className={cn('text-[11px] font-semibold', text)}>{delta}</span>
        </div>
      )}
    </>
  );
}
